import { useMemo, useState, type ComponentType, type ReactNode } from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { Skeleton } from '../Skeleton/Skeleton';
import { Button, type ButtonVariant } from '../Button/Button';
import styles from './Table.module.css';

export interface Column<T> {
  key: keyof T & string;
  header: string;
  width?: string;
  sortable?: boolean;
  render?: (value: T[keyof T], row: T) => ReactNode;
  /** Optional align — defaults to left. */
  align?: 'left' | 'right' | 'center';
}

export interface RowAction<T> {
  label: string;
  icon?: ComponentType<LucideProps>;
  onClick: (row: T) => void;
  variant?: ButtonVariant;
  /** When true the action is hidden for that row. */
  hidden?: (row: T) => boolean;
  /** Per-row disabled state — button is rendered greyed-out + non-clickable. */
  disabled?: (row: T) => boolean;
  /** Native tooltip text for the disabled state (or any state). Lets the
   *  caller explain *why* an action is disabled (e.g. self-approval guard). */
  title?: (row: T) => string | undefined;
}

export interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  /** Stable per-row key (e.g. row.id). */
  keyField: (row: T) => string | number;
  loading?: boolean;
  emptyState?: ReactNode;
  onRowClick?: (row: T) => void;
  rowActions?: (row: T) => RowAction<T>[];
  stickyHeader?: boolean;
  zebra?: boolean;
  className?: string;
  /** Default columns to render in skeleton state. */
  skeletonRows?: number;
  ariaLabel?: string;
}

type SortDir = 'asc' | 'desc';

function compareValues(a: unknown, b: unknown): number {
  if (a === b) return 0;
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

export function Table<T>({
  columns,
  data,
  keyField,
  loading = false,
  emptyState,
  onRowClick,
  rowActions,
  stickyHeader = false,
  zebra = false,
  className,
  skeletonRows = 5,
  ariaLabel,
}: TableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  function toggleSort(col: Column<T>) {
    if (!col.sortable) return;
    if (sortKey === col.key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(col.key);
      setSortDir('asc');
    }
  }

  const sortedData = useMemo(() => {
    if (!sortKey) return data;
    const col = columns.find((c) => c.key === sortKey);
    if (!col) return data;
    const next = [...data].sort((a, b) =>
      compareValues(a[col.key], b[col.key]) * (sortDir === 'asc' ? 1 : -1),
    );
    return next;
  }, [data, sortKey, sortDir, columns]);

  const colSpan = columns.length + (rowActions ? 1 : 0);

  return (
    <div className={cn(styles.wrapper, className)}>
      <div className={styles.scroll}>
        <table className={styles.table} aria-label={ariaLabel}>
          <thead className={cn(styles.thead, stickyHeader && styles.sticky)}>
            <tr>
              {columns.map((col) => {
                const isActive = sortKey === col.key;
                const SortIcon = isActive
                  ? sortDir === 'asc'
                    ? ChevronUp
                    : ChevronDown
                  : ChevronsUpDown;
                return (
                  <th
                    key={col.key}
                    style={col.width ? { width: col.width, textAlign: col.align ?? 'left' } : { textAlign: col.align ?? 'left' }}
                    className={cn(styles.th, col.sortable && styles.thSortable)}
                    onClick={() => toggleSort(col)}
                    aria-sort={
                      col.sortable
                        ? isActive
                          ? sortDir === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                        : undefined
                    }
                  >
                    {col.header}
                    {col.sortable ? (
                      <span
                        aria-hidden
                        className={cn(styles.sortIndicator, isActive && styles.sortActive)}
                      >
                        <SortIcon size={12} />
                      </span>
                    ) : null}
                  </th>
                );
              })}
              {rowActions ? (
                <th
                  className={styles.th}
                  style={{ textAlign: 'right', width: '1%' }}
                  aria-label="Actions"
                />
              ) : null}
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: skeletonRows }).map((_, idx) => (
                  <tr key={`sk-${idx}`} className={styles.row}>
                    {columns.map((col) => (
                      <td key={col.key} className={styles.skeletonCell}>
                        <Skeleton width="80%" height={14} />
                      </td>
                    ))}
                    {rowActions ? <td className={styles.skeletonCell} /> : null}
                  </tr>
                ))
              : sortedData.length === 0
                ? emptyState
                  ? (
                    <tr>
                      <td colSpan={colSpan} className={styles.empty}>
                        {emptyState}
                      </td>
                    </tr>
                  )
                  : null
                : sortedData.map((row, idx) => {
                    const actions = rowActions?.(row) ?? [];
                    const visibleActions = actions.filter((a) => !a.hidden?.(row));
                    const alwaysVisible = visibleActions.length <= 2;
                    return (
                      <tr
                        key={keyField(row)}
                        className={cn(
                          styles.row,
                          zebra && styles.zebra,
                          onRowClick && styles.clickable,
                          idx === sortedData.length - 1 && styles.lastRow,
                        )}
                        onClick={onRowClick ? () => onRowClick(row) : undefined}
                      >
                        {columns.map((col) => {
                          const value = row[col.key];
                          return (
                            <td
                              key={col.key}
                              style={{ textAlign: col.align ?? 'left' }}
                              className={styles.td}
                            >
                              {col.render ? col.render(value, row) : (value as ReactNode)}
                            </td>
                          );
                        })}
                        {rowActions ? (
                          <td
                            className={styles.td}
                            style={{ textAlign: 'right' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div
                              className={cn(
                                styles.actionsCell,
                                alwaysVisible && styles.actionsAlwaysVisible,
                              )}
                            >
                              {visibleActions.map((action) => {
                                const isDisabled = action.disabled?.(row) ?? false;
                                const titleText = action.title?.(row);
                                return (
                                  <Button
                                    key={action.label}
                                    size="sm"
                                    variant={action.variant ?? 'ghost'}
                                    icon={action.icon}
                                    onClick={() => action.onClick(row)}
                                    aria-label={action.label}
                                    disabled={isDisabled}
                                    title={titleText}
                                  >
                                    {action.icon ? null : action.label}
                                  </Button>
                                );
                              })}
                            </div>
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
